import React, { useState, useContext, useEffect } from 'react';
import PropTypes from 'prop-types';
import { DataGrid } from '@mui/x-data-grid';
import Paper from '@mui/material/Paper';
import { Button } from '@mui/material';
import { Link, useNavigate } from 'react-router-dom';
import { getCompanyList } from '../../api/dashboard';
import { deleteCompanySettings } from '../../api/settings';
import { StoreContext } from '../../components/Store';
import { isEORIUser, is360PayStaff } from '../../utils/roles';
import RestoreCompanyModal from './RestoreCompanyModal';

const CustomerTable = () => {
  const [companyList, setCompanyList] = useState([]);
  const store = useContext(StoreContext);
  const navigate = useNavigate();

  const [companyToRestore, setCompanyToRestore] = useState(null);
  const [isRestoreModalOpen, setIsRestoreModalOpen] = useState(false);

  const fetchCompanyList = async () => {
    await getCompanyList()
      .then((data) => {
        store.companyList[1](data.data);
        setCompanyList(data.data);
      })
      .catch((err) => store.showError(err.message));
  };

  useEffect(() => {
    fetchCompanyList();
  }, []);

  const navigateToCustomer = (dataClickedItem) => {
    const companyId = dataClickedItem.row.companyId;
    store.eoriCompany[1](companyId);
    sessionStorage.setItem('eoriSelectedCompanyId', companyId);
    navigate('/CustomerSettings', { state: { res: dataClickedItem.row } });
  };

  const deleteCompanyId = (event, item) => {
    event.preventDefault();
    event.stopPropagation();
    deleteCompanySettings(item.row.companyId).then(() => {
      fetchCompanyList();
    });
  };

  const columns = [
    {
      field: 'companyEori',
      headerName: 'EORI',
      editable: true,
      headerClassName: 'theme--header',
      flex: 1,
    },
    {
      field: 'companyName',
      headerName: 'Name',
      headerClassName: 'theme--header',
      flex: 1,
      editable: true,
    },
    {
      field: 'customerType',
      headerName: 'Customer Type',
      headerClassName: 'theme--header',
      flex: 1,
      editable: true,
    },
    {
      field: 'customerReference',
      headerName: 'Customer Reference',
      headerClassName: 'theme--header',
      flex: 1,
      editable: true,
    },
    {
      field: 'status',
      headerName: 'Status',
      headerClassName: 'theme--header',
      flex: 1,
      editable: true,
    },
    {
      field: 'actions',
      headerName: 'Actions',
      headerClassName: 'theme--header',
      flex: 1,
      editable: true,
      renderCell: (params) => (
        <CustomerActions
          params={params}
          isSelectAllowed={isEORIUser()}
          isDeleteAllowed={params.row.isDeleteAllowed && !is360PayStaff()}
          isRestoreAllowed={!is360PayStaff()}
        />
      ),
    },
  ];

  const CustomerActions = ({ isSelectAllowed, isDeleteAllowed, isRestoreAllowed, params }) => {
    const actions = [];
    const isDeleted = params.row.status === 'Deleted';

    if (isSelectAllowed && !isDeleted) {
      actions.push(
        <Button variant="text" size="small" onClick={() => navigateToCustomer(params)}>
          Select
        </Button>
      );
    }

    if (isDeleteAllowed && !isDeleted) {
      actions.push(
        <Button variant="text" size="small" onClick={(event) => deleteCompanyId(event, params)}>
          Delete
        </Button>
      );
    }

    if (isDeleted && isRestoreAllowed) {
      actions.push(
        <Button
          variant="text"
          size="small"
          onClick={() => {
            setCompanyToRestore(params.row);
            setIsRestoreModalOpen(true);
          }}
        >
          Restore
        </Button>
      );
    }

    return (
      <>
        {actions.map((action, idx) => (
          <React.Fragment key={idx}>
            {action}
            {idx < actions.length - 1 && <span style={{ paddingLeft: 5, paddingRight: 5 }}>|</span>}
          </React.Fragment>
        ))}
      </>
    );
  };

  CustomerActions.propTypes = {
    isSelectAllowed: PropTypes.bool.isRequired,
    isDeleteAllowed: PropTypes.bool.isRequired,
    isRestoreAllowed: PropTypes.bool.isRequired,
    params: PropTypes.object.isRequired,
  };

  return (
    <>
      <RestoreCompanyModal
        isOpen={isRestoreModalOpen}
        setIsOpen={setIsRestoreModalOpen}
        company={companyToRestore}
        refreshCompanies={fetchCompanyList}
      />

      <div className="hyperlinks">
        <span>Select a customer to continue..</span>
      </div>
      <Paper sx={{ width: '100%' }}>
        {companyList?.length > 0 && (
          <DataGrid
            rows={companyList}
            columns={columns}
            getRowId={(row) => row.companyId}
            initialState={{ pagination: { page: 0, pageSize: 50 } }}
            pageSizeOptions={[20, 50, 100]}
            sx={{ border: 0 }}
          />
        )}
      </Paper>
    </>
  );
};

export default CustomerTable;
